<template>
  <div
    :id="clickable ? buttonId : undefined"
    class="overflow-hidden rounded-3xl border border-solid border-gray-500 bg-white transition-shadow tablet:flex"
    :class="[
      clickable &&
        'group cursor-pointer hover:shadow-card hover:shadow-blue-400_a3',
      hasShadow && 'shadow-card',
    ]"
    :title="clickable ? title : ''"
    @click="handleClick"
  >
    <ul
      class="tablet:flex tablet:w-1/3 tablet:shrink-0 tablet:flex-col tablet:p-4 desktop:p-5"
    >
      <li
        v-for="metric in metrics"
        :key="metric.description"
        class="border-b border-solid border-gray-500 px-3 py-3 tablet:flex tablet:flex-1 tablet:flex-col tablet:justify-center tablet:px-0 tablet:py-2 tablet:last:border-b-0"
      >
        <div>
          <p
            class="mb-2 w-fit truncate bg-gradient-to-r from-brand-400 to-cyan-400 bg-clip-text text-3xl font-bold text-transparent tablet:text-4xl tablet:leading-[48px]"
          >
            {{ metric.name }}
          </p>
          <p
            class="w-full text-sm leading-6 text-gray-700 tablet:line-clamp-3 desktop:line-clamp-5"
          >
            {{ metric.description }}
          </p>
        </div>
      </li>
    </ul>

    <TheNuxtLink
      v-if="buttonUrl"
      :id="!clickable ? buttonId : undefined"
      ref="linkRef"
      :to="buttonUrl"
      :title="!clickable ? buttonName : ''"
      class="hidden"
    />

    <div
      class="w-full tablet:flex tablet:w-2/3 tablet:flex-col tablet:justify-between"
    >
      <div class="p-5">
        <h3 class="text-h5 text-center tablet:line-clamp-3">
          {{ title }}
        </h3>
      </div>
      <TheMedia
        :title="title"
        :description="''"
        :video-data="videoData"
        :thumbnail="imageUrl"
        :has-arrow-button="true"
        :theme="theme"
        class="aspect-video w-full rounded-3xl tablet:aspect-[4/3] desktop:aspect-video"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
  import type { RouteLocationRaw } from 'vue-router';

  import type { IVideoData } from '@/components/Shared/Lightbox/Video.vue';
  import TheNuxtLink from '@/components/TheNuxtLink.vue';

  interface IMetric {
    name: string;
    description: string;
  }

  interface IProps {
    title: string;
    metrics: IMetric[];
    buttonId?: string;
    buttonUrl?: RouteLocationRaw;
    buttonName?: string;
    imageUrl: string;
    videoUrl?: string;
    clickable?: boolean;
    hasShadow?: boolean;
    theme?: TTheme;
  }

  const props = withDefaults(defineProps<IProps>(), {
    videoUrl: undefined,
    buttonId: undefined,
    buttonUrl: undefined,
    buttonName: undefined,
    clickable: false,
    hasShadow: true,
    theme: 'blue',
  });

  const videoData = computed((): IVideoData | undefined =>
    props.videoUrl
      ? {
          title: props.title,
          description: '',
          url: props.videoUrl,
          imageUrl: props.imageUrl,
          buttonPosition: 'center',
        }
      : undefined
  );

  const linkRef = ref<InstanceType<typeof TheNuxtLink> | null>(null);

  const handleClick = (event: MouseEvent): void => {
    const linkEl = linkRef.value?.$el as HTMLElement | undefined;
    const isOutsideLink = linkEl && !linkEl.contains(event.target as Node);
    // 避免點擊連結本身時再次觸發，造成無窮迴圈
    if (props.clickable && props.buttonUrl && isOutsideLink) {
      linkEl.click();
    }
  };

  export type { IMetric };
</script>
